import { RemoI18nLanguagesEnum, useLanguage, useTranslate } from "@obsbot/remo-i18n";
import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { UserSettingLayoutContainer } from "../UserSettingLayoutContainer";
import "./index.less";
import { RecordItem, UpdateRecord } from "remo-components";
import { checkRecentlyVersionInfo } from "@obsbot/remo-server-api";
import { invoke, listenEvent, removeEvent } from "../../utils/ipc";
import { eventBus } from "@obsbot/remo-utils";
import classNames from "classnames";

const SoftwareVersionPage = (): ReactElement => {
    const t = useTranslate();
    const lang = useLanguage();
    const [updateStatus, setUpdateStatus] = useState<
        "idle" | "checking" | "downloading" | "ready" | "error"
    >("idle");
    const [progress, setProgress] = useState(0);
    const [changelogs, setChangeLogs] = useState<RecordItem[]>([]);

    useEffect(() => {
        listenEvent("update-pack-info", ({ type, percent }) => {
            if (type === "downloading" && percent) {
                setProgress(percent);
                setUpdateStatus("downloading");
                if (percent >= 100) {
                    setUpdateStatus("idle");
                }
            }
        });
        return () => {
            removeEvent("update-pack-info");
        };
    }, []);

    // 点击按钮：检查是否存在更新
    const onPrimaryClick = async (): Promise<void> => {
        if (updateStatus !== "idle") {
            return;
        }
        setUpdateStatus("checking");
        const versionInfo = await invoke("recheck-update-version");
        setUpdateStatus("idle");
        if (!versionInfo) {
            eventBus.emit("show-message", {
                message: t("settings.software-version.alreadyNew"),
                type: "warning",
            });
        }
    };

    const formatLang = useMemo(() => {
        switch (lang) {
            case RemoI18nLanguagesEnum.zhCN: {
                return "zh-CN";
            }
            case RemoI18nLanguagesEnum.zhHant: {
                return "zh-TW";
            }
            case RemoI18nLanguagesEnum.en: {
                return "en-US";
            }
            case RemoI18nLanguagesEnum.KoKR: {
                return "ko-KR";
            }
            case RemoI18nLanguagesEnum.ar: {
                return "ar-AE";
            }
            default: {
                return "en-US";
            }
        }
    }, [lang]);

    useEffect(() => {
        const handleCheckUpdate = async (): Promise<void> => {
            const updateInfo = await checkRecentlyVersionInfo();
            const curLangChangelog: RecordItem[] = updateInfo.results.map(info => {
                const curLangDesc = info.update_desc_list.find(desc => desc.lan === formatLang);
                return {
                    content: curLangDesc?.desc.replaceAll("\\n", "\n").split("\n") ?? [""],
                    version: info.version,
                    update_time: info.update_time,
                    subtitle: info.desc,
                };
            });
            setChangeLogs(curLangChangelog);
        };
        handleCheckUpdate();
    }, [formatLang]);

    return (
        <UserSettingLayoutContainer>
            <div className="software-version-container">
                <div className="version-content">
                    <div className="version-title-left">
                        <div className="setting-page-title">
                            {t("settings.software-version.title")}
                        </div>
                        <div className="current-version">
                            {t("settings.software-version.currentVersion")}:
                            {import.meta.env.APP_VERSION}
                        </div>
                    </div>
                    {(window as any).isElectron && (
                        <div
                            className={classNames("check-version-btn", {
                                disabled: updateStatus !== "idle",
                                downloading: updateStatus === "downloading",
                            })}
                            // @ts-ignore
                            style={{ "--download-percent": progress + "%" }}
                            onClick={onPrimaryClick}
                        >
                            <span>
                                {updateStatus === "checking"
                                    ? t("settings.software-version.checking")
                                    : updateStatus === "downloading"
                                      ? `${Math.round(progress)}%`
                                      : t("settings.software-version.checkUpdate")}
                            </span>
                        </div>
                    )}
                </div>
                <div
                    className={`setting-page-content ${changelogs.length === 0 ? "setting-page-content-empty" : ""}`}
                >
                    <UpdateRecord recordList={changelogs} />
                </div>
            </div>
        </UserSettingLayoutContainer>
    );
};

export default SoftwareVersionPage;
